import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui";

const Login = () => {
  const { user, initializing, login, signup, signInWithOAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialTab = location.state?.tab === "signup" ? "signup" : "signin";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formValues, setFormValues] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showSigninPassword, setShowSigninPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);

  const redirectPath = useMemo(
    () => location.state?.from?.pathname || "/dashboard",
    [location.state]
  );

  useEffect(() => {
    if (!initializing && user) {
      navigate(redirectPath, { replace: true });
    }
  }, [user, initializing, navigate, redirectPath]);

  useEffect(() => {
    if (location.state?.passwordReset) {
      setMessage("Password updated! Please sign in with your new password.");
      navigate(location.pathname, {
        replace: true,
        state: location.state?.from ? { from: location.state.from } : null,
      });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (location.state?.tab) {
      navigate(location.pathname, {
        replace: true,
        state: location.state?.from ? { from: location.state.from } : null,
      });
    }
  }, [location, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (activeTab === "signin") {
        await login(formValues.email, formValues.password);
      } else {
        const { user: newUser } = await signup(
          formValues.email,
          formValues.password
        );
        if (newUser) {
          setMessage(
            "Account created! Check your email to confirm before logging in."
          );
          setActiveTab("signin");
        } else {
          setMessage(
            "Check your email to confirm your account before signing in."
          );
        }
      }
    } catch (err) {
      console.error("[auth] Failed to authenticate", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setError("");
    setMessage("");
  };

  const handleOAuthSignIn = async (provider) => {
    setError("");
    setOauthLoading(provider);
    try {
      await signInWithOAuth(provider);
    } catch (err) {
      console.error(`[auth] OAuth sign-in failed for ${provider}`, err);
      setError(err.message || "OAuth sign-in failed. Please try again.");
      setOauthLoading(null);
    }
  };

  if (initializing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-soft">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-soft px-4 py-16">
      <div
        className="absolute inset-0 -z-10 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at top, rgba(251, 191, 36, 0.35), transparent 45%), radial-gradient(circle at bottom, rgba(244, 114, 182, 0.25), transparent 55%)",
        }}
      />
      <div className="w-full max-w-lg">
        <div className="mb-12 flex justify-center">
          <div className="flex h-32 w-32 items-center justify-center rounded-[3.75rem] border-2 border-primary/40 bg-white shadow-2xl shadow-brand/40">
            <img
              src="/media/logo.png"
              alt="DoodleMagic Studio logo"
              className="h-full w-full rounded-[3.75rem] object-cover"
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="space-y-8">
            <div className="text-center">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                Welcome
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold">
                {activeTab === "signin"
                  ? "Welcome back, story maker!"
                  : "Create your magical studio"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {activeTab === "signin"
                  ? "Sign in to animate your next doodle adventure."
                  : "Sign up to start turning colorful sketches into short films."}
              </p>
            </div>

            <div className="flex justify-center">
              <TabsList>
                <TabsTrigger value="signin">Sign in</TabsTrigger>
                <TabsTrigger value="signup">Sign up</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="signin" className="border-none bg-transparent p-0 shadow-none">
              <Card className="border border-border/60 bg-white/90 shadow-card">
                <CardHeader>
                  <CardTitle>Sign in to continue</CardTitle>
                  <CardDescription>
                    Use your email and password to enter the magic studio.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="signin-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={formValues.email}
                          onChange={handleChange}
                          className="pl-10"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="signin-password"
                          name="password"
                          type={showSigninPassword ? "text" : "password"}
                          autoComplete="current-password"
                          required
                          value={formValues.password}
                          onChange={handleChange}
                          className="pl-10"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                          onClick={() => setShowSigninPassword((previous) => !previous)}
                          aria-label={showSigninPassword ? "Hide password" : "Show password"}
                        >
                          {showSigninPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Link
                        to="/reset-password"
                        className="text-sm font-semibold text-primary transition hover:text-primary/80"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    {message && (
                      <p className="text-sm text-emerald-600">{message}</p>
                    )}
                    <Button
                      type="submit"
                      className="h-11 w-full rounded-xl text-sm font-semibold"
                      disabled={loading}
                    >
                      {loading ? "Signing in…" : "Sign in"}
                    </Button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 flex-1 rounded-xl text-sm font-medium"
                        disabled={oauthLoading !== null}
                        onClick={() => handleOAuthSignIn("google")}
                      >
                        {oauthLoading === "google" ? (
                          "Redirecting…"
                        ) : (
                          <>
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                              <path
                                fill="currentColor"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="currentColor"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="currentColor"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="currentColor"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                            Google
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 flex-1 rounded-xl text-sm font-medium"
                        disabled={oauthLoading !== null}
                        onClick={() => handleOAuthSignIn("apple")}
                      >
                        {oauthLoading === "apple" ? (
                          "Redirecting…"
                        ) : (
                          <>
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                            </svg>
                            Apple
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="signup" className="border-none bg-transparent p-0 shadow-none">
              <Card className="border border-border/60 bg-white/90 shadow-card">
                <CardHeader>
                  <CardTitle>Create your account</CardTitle>
                  <CardDescription>
                    You’ll receive 10 magic stars to start animating right away.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={formValues.email}
                          onChange={handleChange}
                          className="pl-10"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          name="password"
                          type={showSignupPassword ? "text" : "password"}
                          autoComplete="new-password"
                          required
                          value={formValues.password}
                          onChange={handleChange}
                          className="pl-10"
                          placeholder="Create a secure password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                          onClick={() => setShowSignupPassword((previous) => !previous)}
                          aria-label={showSignupPassword ? "Hide password" : "Show password"}
                        >
                          {showSignupPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    {message && (
                      <p className="text-sm text-emerald-600">{message}</p>
                    )}
                    <Button
                      type="submit"
                      className="h-11 w-full rounded-xl text-sm font-semibold"
                      disabled={loading}
                    >
                      {loading ? "Creating account…" : "Create account"}
                    </Button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">Or sign up with</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 flex-1 rounded-xl text-sm font-medium"
                        disabled={oauthLoading !== null}
                        onClick={() => handleOAuthSignIn("google")}
                      >
                        {oauthLoading === "google" ? (
                          "Redirecting…"
                        ) : (
                          <>
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                              <path
                                fill="currentColor"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="currentColor"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="currentColor"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="currentColor"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                            Google
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 flex-1 rounded-xl text-sm font-medium"
                        disabled={oauthLoading !== null}
                        onClick={() => handleOAuthSignIn("apple")}
                      >
                        {oauthLoading === "apple" ? (
                          "Redirecting…"
                        ) : (
                          <>
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                            </svg>
                            Apple
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>

        <div className="mt-10 space-y-3 text-center text-sm text-muted-foreground">
          <p>
            <Link to="/" className="font-semibold text-primary hover:text-primary/80">
              Back to the homepage
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;

